/**
 * A coroutine has two uses, one is to be able to cancel a coroutine
 * and the other is to check if a coroutine ran to completion.
 * When implementing a coroutine (generator function) the user can provide a token that is then checked between each tick,
 * if it has been canceled the coroutine should just return instead of yielding to stop futher execution.
 * If the coroutine accepts a token you should also make sure to complete the token at the end of the coroutine.
 */
export class CoroutineToken {
    constructor() {
        this._canceled = false;
        this._completed = false;
    }
    cancel() {
        this._canceled = true;
    }
    complete() {
        this._completed = true;
    }
    get is_canceled() {
        return this._canceled;
    }
    get is_completed() {
        return this._completed;
    }
}
export class CoroutineManager {
    constructor() {
        /** Holds the coroutines that should currently be stepped */
        this._active_coroutines = new Set();
        /**
         * Table with child to parent lookup for coroutines that have yileded other coroutines.
         * @key the yielded coroutine that will run to completion before the parent is continued
         * @value the parent coroutine that yielded the nested coroutine
         */
        this._nested_coroutines = new Map();
        /** Array for storing coroutines that should be added to active coroutines after frame update */
        this._cached_array = [];
    }
    /**
     * This should only be called from the Viewer update loop.
     * @param timestep the time elapsed since the last frame in milliseconds
     */
    update(timestep) {
        this._cached_array.length = 0;
        this._active_coroutines.forEach(coroutine => {
            let result;
            try {
                result = coroutine.next(timestep);
            }
            catch (e) {
                console.error('Failed to run a coroutine: ', e);
                this.dispose_active_coroutine_chain(coroutine);
                return;
            }
            if (result.done) { // Remove coroutines that have finished
                this._active_coroutines.delete(coroutine);
                // Do we have a parent coroutine to push on the stack?
                const parent = this._nested_coroutines.get(coroutine);
                if (parent !== undefined) {
                    this._nested_coroutines.delete(coroutine);
                    // Readding during iteration will mean that we also step the parent once in this frame.
                    // That is not what we want so we add it to a list to be pushed after iteration
                    this._cached_array.push(parent);
                }
            }
            else if (result.value !== undefined) { // Nesting coroutine
                this._nested_coroutines.set(result.value, coroutine);
                this._active_coroutines.delete(coroutine);
                this._cached_array.push(result.value);
            }
        });
        for (let i = 0; i < this._cached_array.length; i++) {
            this._active_coroutines.add(this._cached_array[i]);
        }
    }
    start_coroutine(coroutine) {
        this._active_coroutines.add(coroutine);
    }
    stop_and_remove_all_coroutines() {
        this._active_coroutines.clear();
        this._nested_coroutines.clear();
        this._cached_array.length = 0;
    }
    dispose() {
        this.stop_and_remove_all_coroutines();
    }
    /**
     * Disposes an active coroutine and removes all its parents.
     * @param coroutine Active coroutine.
     */
    dispose_active_coroutine_chain(coroutine) {
        this._active_coroutines.delete(coroutine);
        let parent;
        while ((parent = this._nested_coroutines.get(coroutine))) {
            this._nested_coroutines.delete(coroutine);
            coroutine = parent;
        }
    }
}
//# sourceMappingURL=CoroutineManager.js.map